import { Layer, LayerPropertyDef } from "../internal";
import WFS from "ol/format/WFS";
import GML32 from "ol/format/GML32";
import GML3 from "ol/format/GML3";
import GML2 from "ol/format/GML2";
import GeoJSON from "ol/format/GeoJSON";
import { extentToOLExtent } from "../utils";
import Projection from "ol/proj/Projection";
import { parseGeoJSON } from "../format/utils";
function parseGetCapabilitesXML(cpb_document) {
    const layers = [];
    let version = "2.0.0";
    const rootName = cpb_document.documentElement.nodeName;
    const root_elements = cpb_document.getElementsByTagNameNS("*", rootName);
    if (root_elements.length > 0) {
        const root_element = root_elements[0];
        version = root_element.getAttribute("version");
    }
    const featureTypes = cpb_document.getElementsByTagNameNS("*", "FeatureType");
    if (featureTypes) {
        for (let i = 0; i < featureTypes.length; i++) {
            const featureType = featureTypes[i];
            const elements_Name = featureType.getElementsByTagNameNS("*", "Name");
            const elements_Title = featureType.getElementsByTagNameNS("*", "Title");
            layers.push({
                layername: elements_Name.length > 0 ? elements_Name[0].textContent : "",
                title: elements_Title.length > 0 ? elements_Title[0].textContent : "",
                editable: false,
                coordsdim: 2,
                version: version,
                format: "application/gml+xml; version=3.2",
            });
        }
    }
    const supported_formats = [];
    const operations = cpb_document.getElementsByTagNameNS("*", "Operation");
    if (operations.length > 0) {
        for (const key in operations) {
            const operation = operations[key];
            if (operation.getAttribute("name") == "GetFeature") {
                const parameters = operation.getElementsByTagNameNS("*", "Parameter");
                for (const key2 in parameters) {
                    const parameter = parameters[key2];
                    if (parameter.getAttribute("name") == "outputFormat") {
                        const values = parameter.getElementsByTagNameNS("*", "Value");
                        for (const key3 in values) {
                            if (values[key3].textContent)
                                supported_formats.push(values[key3].textContent);
                        }
                        break;
                    }
                }
                break;
            }
        }
    }
    if (supported_formats.length == 0) {
        const result_format_elements = cpb_document.getElementsByTagNameNS("*", "ResultFormat");
        for (const key in result_format_elements) {
            const result_format_element = result_format_elements[key];
            for (const key2 in result_format_element.childNodes) {
                const childNode = result_format_element.childNodes[key2];
                if (childNode.tagName)
                    supported_formats.push(childNode.tagName);
            }
        }
    }
    if (supported_formats.length > 0) {
        let format = supported_formats[0];
        const filtered_supported_formats = supported_formats.filter((sformat) => {
            return sformat.indexOf("gml") > -1 && sformat.indexOf("3.2") > -1;
        });
        if (filtered_supported_formats.length > 0) {
            format = filtered_supported_formats[0];
        }
        for (const layer of layers) {
            layer.format = format;
        }
    }
    return layers;
}
async function parse_xml(response) {
    return response
        .text()
        .then((str) => new window.DOMParser().parseFromString(str, "application/xml"))
        .then((xml_document) => {
        const error_node = xml_document.getElementsByTagName("parsererror");
        if (error_node.length > 0) {
            throw new Error("Error while parsing the response!");
        }
        return xml_document;
    });
}
async function parse_json(response) {
    const geojson_features = await response.json();
    const parsed_geojson_data = parseGeoJSON(geojson_features, false, true);
    if (parsed_geojson_data.valid) {
        Layer.geojsonRemoveZValues(parsed_geojson_data.data);
        return parsed_geojson_data.data;
    }
    else {
        throw new Error(parsed_geojson_data.message);
    }
}
function parse_gml(gml_document, projection, version, format) {
    let wfsformat = new WFS({
        gmlFormat: new GML32({
            hasZ: false,
        }),
        version: version,
    });
    if (format.indexOf("3.1") > -1) {
        wfsformat = new WFS({ gmlFormat: new GML3(), version: version });
    }
    else if (format.indexOf("GML2") > -1) {
        const gmlformat = new GML2();
        gmlformat.namespace = "";
        wfsformat = new WFS({ gmlFormat: gmlformat, version: version });
    }
    const features = wfsformat.readFeatures(gml_document, {
        featureProjection: projection,
    });
    const geojson_features = new GeoJSON().writeFeaturesObject(features, {
        dataProjection: new Projection({ code: projection }),
        featureProjection: new Projection({ code: projection }),
    });
    const parsed_geojson_data = parseGeoJSON(geojson_features, false, true);
    if (parsed_geojson_data.valid) {
        Layer.geojsonRemoveZValues(parsed_geojson_data.data);
        return parsed_geojson_data.data;
    }
    else {
        throw new Error(parsed_geojson_data.message);
    }
}
export async function readWFSGetCapabilities(url, version = "2.0.0", fetch_options = null) {
    const capabilites_url = new URL(url);
    capabilites_url.searchParams.set("service", "WFS");
    capabilites_url.searchParams.set("version", version);
    capabilites_url.searchParams.set("request", "GetCapabilities");
    return fetch(capabilites_url.toString(), fetch_options).then((response) => response
        .text()
        .then((str) => new window.DOMParser().parseFromString(str, "text/xml"))
        .then((cpb_document) => {
        const error_node = cpb_document.getElementsByTagName("parsererror");
        if (error_node.length > 0)
            throw new Error("Error while parsing the response!");
        return parseGetCapabilitesXML(cpb_document);
    }));
}
export async function readWFSDescribeFeatureType(url, typeName, version = "2.0.0", fetch_options = null) {
    const fetchurl = new URL(url);
    fetchurl.searchParams.set("service", "WFS");
    fetchurl.searchParams.set("version", version);
    fetchurl.searchParams.set("request", "DescribeFeatureType");
    const typeName_attr = version == "2.0.0" ? "typeNames" : "typeName";
    fetchurl.searchParams.set(typeName_attr, typeName);
    return fetch(fetchurl.toString(), fetch_options).then((response) => response
        .text()
        .then((str) => new window.DOMParser().parseFromString(str, "text/xml"))
        .then((dft_document) => {
        const error_node = dft_document.getElementsByTagName("parsererror");
        if (error_node.length > 0)
            throw new Error("Error while parsing the response!");
        return {
            layername: typeName,
            propertydef: LayerPropertyDef.propertydefFromLDescribeFeaturetypeNode(dft_document),
        };
    }));
}
export async function readGeometryTypeFromFirstFeature(url, typeName, version = "2.0.0", format = "geojson", fetch_options = null) {
    const get_feature = async () => {
        const fetchurl = new URL(url);
        fetchurl.searchParams.set("service", "WFS");
        fetchurl.searchParams.set("version", version);
        fetchurl.searchParams.set("request", "GetFeature");
        const typeName_attr = version == "2.0.0" ? "typeNames" : "typeName";
        fetchurl.searchParams.set(typeName_attr, typeName);
        fetchurl.searchParams.set("outputFormat", format);
        const count_attr = version == "2.0.0" ? "count" : "maxFeatures";
        fetchurl.searchParams.set(count_attr, "1");
        return fetch(fetchurl.toString(), fetch_options);
    };
    const parse_gml = async (response) => {
        return response
            .text()
            .then((str) => new window.DOMParser().parseFromString(str, "text/xml"))
            .then((gml_document) => {
            const error_node = gml_document.getElementsByTagName("parsererror");
            if (error_node.length > 0)
                throw new Error("Error while parsing the response!");
            let wfsformat = new WFS({ gmlFormat: new GML32(), version: version });
            if (format.indexOf("3.1") > -1) {
                wfsformat = new WFS({ gmlFormat: new GML3(), version: version });
            }
            else if (format.indexOf("GML2") > -1) {
                wfsformat = new WFS({ gmlFormat: new GML2(), version: version });
            }
            const features = wfsformat.readFeatures(gml_document);
            const geojson_features = new GeoJSON().writeFeaturesObject(features);
            return geojson_features;
        });
    };
    const parse_json = async (response) => {
        return response.json();
    };
    const response = await get_feature();
    let geojson = null;
    if (format.indexOf("gml") > -1 || format.toLowerCase().indexOf("gml") > -1) {
        geojson = await parse_gml(response);
    }
    else if (format == "geojson") {
        geojson = await parse_json(response);
    }
    else {
        throw new Error("format is not supported");
    }
    if (geojson) {
        if (geojson.features.length > 0) {
            const geomtype = geojson.features[0].geometry.type;
            return {
                layername: typeName,
                geometrytype: geomtype,
            };
        }
    }
    throw new Error("could not parse geometrytype from first feature");
}
export async function readWFS20LayerOptions(url, typeName, format = "geojson", version = "2.0.0", fetch_options = null) {
    /*     const options_cpb = await readWFSGetCapabilities(url); */
    const options_dft = await readWFSDescribeFeatureType(url, typeName, version, fetch_options);
    const options_ff = await readGeometryTypeFromFirstFeature(url, typeName, version, format, fetch_options);
    return { ...options_dft, ...options_ff };
}
export function getWFS20Features(baseUrl, extent, srid, layername, onSuccess, onError, format = "geojson", version = "2.0.0", fetch_options = null, namespaces = "") {
    const projection = `EPSG:${srid}`;
    const olextent = extentToOLExtent(extent);
    const wfsprojectionstring = `urn:ogc:def:crs:EPSG::${srid}`;
    const get_feature = async () => {
        const url = new URL(baseUrl);
        url.searchParams.set("service", "WFS");
        url.searchParams.set("version", version);
        url.searchParams.set("request", "GetFeature");
        const typeName_attr = version == "2.0.0" ? "typeNames" : "typeName";
        url.searchParams.set(typeName_attr, layername);
        url.searchParams.set("outputFormat", format);
        url.searchParams.set("srsName", wfsprojectionstring);
        if (namespaces)
            url.searchParams.set("namespaces", namespaces);
        url.searchParams.set("bbox", olextent.join(","));
        return fetch(url.toString(), fetch_options);
    };
    if (format.indexOf("gml") > -1 || format.toLowerCase().indexOf("gml") > -1) {
        get_feature()
            .then((response) => parse_xml(response))
            .then((gml_document) => {
            const geojson_features = parse_gml(gml_document, projection, version, format);
            onSuccess(geojson_features);
        })
            .catch(onError);
    }
    else if (format == "geojson" || format == "json") {
        get_feature()
            .then((response) => parse_json(response))
            .then((geojson) => {
            for (const feature of geojson.features) {
                if (feature.id === undefined) {
                    if ("objectid" in feature.properties) {
                        feature.id = feature.properties.objectid;
                    }
                    else if ("OBJECTID" in feature.properties) {
                        feature.id = feature.properties.OBJECTID;
                    }
                }
            }
            onSuccess(geojson);
        })
            .catch(onError);
    }
}
export function pageWFS20Features(baseurl, typeName, start, limit, srid, countonly, onSuccess, onError, format = "application/gml+xml; version=3.2", version = "2.0.0", fetch_options = null) {
    const projection = `EPSG:${srid}`;
    const get_feature = async () => {
        const url = new URL(baseurl);
        url.searchParams.set("service", "WFS");
        url.searchParams.set("version", version);
        url.searchParams.set("request", "GetFeature");
        const typeName_attr = version == "2.0.0" ? "typeNames" : "typeName";
        url.searchParams.set(typeName_attr, typeName);
        if (!countonly) {
            url.searchParams.set("outputFormat", format);
            url.searchParams.set("srsName", projection);
            url.searchParams.set("STARTINDEX", start.toString());
            url.searchParams.set("COUNT", limit.toString());
        }
        url.searchParams.set("RESULTTYPE", countonly ? "hits" : "results");
        return fetch(url.toString(), fetch_options);
    };
    if (format.indexOf("gml") > -1 || format.toLowerCase().indexOf("gml") > -1) {
        get_feature()
            .then((response) => parse_xml(response))
            .then((gml_document) => {
            if (countonly) {
                const fc = gml_document.getElementsByTagName("wfs:FeatureCollection");
                if (fc.length == 0) {
                    throw new Error("wfs:FeatureCollection not found in response");
                }
                onSuccess({ type: "FeatureCollection", features: [] }, parseInt(fc[0].getAttribute("numberMatched")));
            }
            else {
                const geojson_features = parse_gml(gml_document, projection, version, format);
                onSuccess(geojson_features, 10);
            }
        })
            .catch(onError);
    }
    else if (format == "geojson" || format == "json") {
        get_feature()
            .then((response) => parse_json(response))
            .then((geojson) => {
            for (const feature of geojson.features) {
                if (feature.id === undefined) {
                    if ("objectid" in feature.properties) {
                        feature.id = feature.properties.objectid;
                    }
                    else if ("OBJECTID" in feature.properties) {
                        feature.id = feature.properties.OBJECTID;
                    }
                    else {
                        throw new Error("geojson has no id");
                    }
                }
            }
            onSuccess(geojson, 0);
        })
            .catch(onError);
    }
}
//# sourceMappingURL=wfsutils.js.map